import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatApiErrorDetail } from '@/lib/api';
import { fmtBRL, fmtDate, statusLabel, statusColor, todayISO } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Eye, Ban, CheckCircle2, Paperclip, Upload, Download, Trash2 } from 'lucide-react';
import { TX } from '@/constants/testIds';
import { toast } from 'sonner';

const emptyForm = {
  description: '', amount: '', due_date: todayISO(), competence_date: todayISO(),
  account_id: '', category_id: '', party_id: '', notes: '', reference: '',
};

function statusMatchesTab(status, due_date, tab) {
  const today = todayISO();
  const inDays = (n) => {
    const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
  };
  if (tab === 'all') return true;
  if (tab === 'overdue') return status === 'OVERDUE';
  if (tab === 'today') return due_date === today && ['PENDING', 'OVERDUE', 'PARTIAL'].includes(status);
  if (tab === 'week') return due_date >= today && due_date <= inDays(7) && ['PENDING', 'OVERDUE', 'PARTIAL'].includes(status);
  if (tab === 'month') return due_date >= today && due_date <= inDays(30) && ['PENDING', 'OVERDUE', 'PARTIAL'].includes(status);
  if (tab === 'paid') return status === 'PAID';
  if (tab === 'cancelled') return status === 'CANCELLED';
  return true;
}

export default function TransactionsPage({ type }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [payForm, setPayForm] = useState({ amount: '', payment_date: todayISO(), payment_method: 'PIX', account_id: '', notes: '' });

  const { data: accounts } = useQuery({ queryKey: ['accounts'], queryFn: async () => (await api.get('/accounts')).data });
  const { data: cats } = useQuery({ queryKey: ['categories'], queryFn: async () => (await api.get('/categories')).data });
  const { data: parties } = useQuery({ queryKey: ['parties'], queryFn: async () => (await api.get('/parties')).data });

  const partyType = type === 'INCOME' ? 'CUSTOMER' : 'SUPPLIER';
  const filteredCats = (cats || []).filter((c) => c.type === type && c.active !== false);
  const filteredParties = (parties || []).filter((p) => p.party_type === partyType && p.active !== false);

  const { data, isLoading } = useQuery({
    queryKey: ['transactions', type],
    queryFn: async () => (await api.get('/transactions', { params: { type, page_size: 500 } })).data,
  });

  const items = useMemo(() => {
    const list = (data?.items || []).filter((t) => statusMatchesTab(t.status, t.due_date, tab));
    if (!search) return list;
    return list.filter((t) => t.description.toLowerCase().includes(search.toLowerCase()));
  }, [data, tab, search]);

  const accountMap = (accounts || []).reduce((a, x) => (a[x.id] = x, a), {});
  const catMap = (cats || []).reduce((a, x) => (a[x.id] = x, a), {});
  const partyMap = (parties || []).reduce((a, x) => (a[x.id] = x, a), {});

  const createMut = useMutation({
    mutationFn: async (payload) => (await api.post('/transactions', payload)).data,
    onSuccess: () => {
      toast.success('Lançamento criado');
      setModalOpen(false); setForm(emptyForm);
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const payMut = useMutation({
    mutationFn: async ({ txId, payload }) => (await api.post(`/transactions/${txId}/payments`, payload)).data,
    onSuccess: () => {
      toast.success('Pagamento registrado');
      setPayOpen(false);
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['tx-detail'] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const cancelMut = useMutation({
    mutationFn: async (txId) => (await api.post(`/transactions/${txId}/cancel`)).data,
    onSuccess: () => {
      toast.success('Transação cancelada');
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const submitTx = (e) => {
    e.preventDefault();
    createMut.mutate({
      ...form, type,
      amount: parseFloat(form.amount),
      party_id: form.party_id || null,
    });
  };

  const submitPay = (e) => {
    e.preventDefault();
    payMut.mutate({
      txId: selectedTx.id,
      payload: {
        ...payForm,
        amount: parseFloat(payForm.amount),
      },
    });
  };

  const openPay = (tx) => {
    const remaining = Math.max(0, tx.amount - (tx.paid_amount || 0));
    setSelectedTx(tx);
    setPayForm({ amount: remaining.toFixed(2), payment_date: todayISO(), payment_method: 'PIX', account_id: accounts?.[0]?.id || '', notes: '' });
    setPayOpen(true);
  };

  const openNew = () => {
    setForm({ ...emptyForm, account_id: accounts?.[0]?.id || '', category_id: filteredCats[0]?.id || '' });
    setModalOpen(true);
  };

  const openDetail = (tx) => { setSelectedTx(tx); setDetailOpen(true); };

  const title = type === 'INCOME' ? 'Contas a Receber' : 'Contas a Pagar';
  const subtitle = type === 'INCOME' ? 'Receitas pendentes e recebidas' : 'Despesas e pagamentos';

  return (
    <div data-testid={TX.page} className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{subtitle}</div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">{title}</h1>
        </div>
        <Button data-testid={TX.newBtn} onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="h-4 w-4 mr-2" /> {type === 'INCOME' ? 'Nova Receita' : 'Nova Despesa'}
        </Button>
      </div>

      <Card className="p-4 border-border/70">
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all" data-testid={TX.tabAll}>Todas</TabsTrigger>
              <TabsTrigger value="overdue" data-testid={TX.tabOverdue}>Vencidas</TabsTrigger>
              <TabsTrigger value="today" data-testid={TX.tabToday}>Hoje</TabsTrigger>
              <TabsTrigger value="week" data-testid={TX.tabWeek}>Semana</TabsTrigger>
              <TabsTrigger value="month" data-testid={TX.tabMonth}>Mês</TabsTrigger>
              <TabsTrigger value="paid" data-testid={TX.tabPaid}>{type === 'INCOME' ? 'Recebidas' : 'Pagas'}</TabsTrigger>
              <TabsTrigger value="cancelled" data-testid={TX.tabCancelled}>Canceladas</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid={TX.filterSearch} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar descrição..." className="pl-9" />
          </div>
        </div>
      </Card>

      <Card className="border-border/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 border-b">
              <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                <th className="px-4 py-3">Descrição</th>
                <th className="px-4 py-3">{type === 'INCOME' ? 'Cliente' : 'Fornecedor'}</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Vencimento</th>
                <th className="px-4 py-3 text-right">Valor</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Carregando…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Nenhum lançamento.</td></tr>
              ) : items.map((t) => (
                <tr key={t.id} data-testid={TX.row(t.id)} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.description}</div>
                    {t.reference && <div className="text-xs text-muted-foreground">Ref: {t.reference}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{partyMap[t.party_id]?.name || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{catMap[t.category_id]?.name || '—'}</td>
                  <td className="px-4 py-3 font-mono tabular-nums">{fmtDate(t.due_date)}</td>
                  <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${type === 'INCOME' ? 'money-in' : 'money-out'}`}>
                    {fmtBRL(t.amount)}
                    {t.paid_amount > 0 && t.status !== 'PAID' && (
                      <div className="text-[10px] text-muted-foreground font-normal">Pago: {fmtBRL(t.paid_amount)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3"><span className={`chip ${statusColor[t.status]}`}>{statusLabel[t.status]}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button data-testid={TX.viewBtn(t.id)} size="icon" variant="ghost" onClick={() => openDetail(t)} title="Detalhes"><Eye className="h-4 w-4" /></Button>
                      {['PENDING', 'PARTIAL', 'OVERDUE'].includes(t.status) && (
                        <Button data-testid={TX.payBtn(t.id)} size="sm" variant="outline" onClick={() => openPay(t)} className="h-8 px-2 gap-1">
                          <CheckCircle2 className="h-3.5 w-3.5" /> {type === 'INCOME' ? 'Receber' : 'Pagar'}
                        </Button>
                      )}
                      {t.status !== 'CANCELLED' && (
                        <Button data-testid={TX.cancelBtn(t.id)} size="icon" variant="ghost" className="text-rose-500" onClick={() => { if (confirm('Cancelar transação? Pagamentos serão estornados.')) cancelMut.mutate(t.id); }} title="Cancelar">
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* CREATE TX MODAL */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{type === 'INCOME' ? 'Nova Receita' : 'Nova Despesa'}</DialogTitle>
          </DialogHeader>
          <form data-testid={TX.modalForm} onSubmit={submitTx} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Descrição</Label>
                <Input data-testid={TX.modalDescription} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Valor (R$)</Label>
                <Input data-testid={TX.modalAmount} type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="NF, boleto…" />
              </div>
              <div className="space-y-2">
                <Label>Vencimento</Label>
                <Input data-testid={TX.modalDue} type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Competência</Label>
                <Input data-testid={TX.modalCompetence} type="date" required value={form.competence_date} onChange={(e) => setForm({ ...form, competence_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Conta financeira</Label>
                <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                  <SelectTrigger data-testid={TX.modalAccount}><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{(accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid={TX.modalCategory}><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{filteredCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>{type === 'INCOME' ? 'Cliente' : 'Fornecedor'} (opcional)</Label>
                <Select value={form.party_id || 'none'} onValueChange={(v) => setForm({ ...form, party_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid={TX.modalParty}><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nenhum —</SelectItem>
                    {filteredParties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Observações</Label>
                <Textarea data-testid={TX.modalNotes} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button data-testid={TX.modalSubmit} type="submit" disabled={createMut.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {createMut.isPending ? 'Salvando…' : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* PAY MODAL */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{type === 'INCOME' ? 'Registrar Recebimento' : 'Registrar Pagamento'}</DialogTitle>
          </DialogHeader>
          {selectedTx && (
            <div className="text-sm text-muted-foreground mb-3">
              <div className="font-medium text-foreground">{selectedTx.description}</div>
              Valor total: <span className="font-mono">{fmtBRL(selectedTx.amount)}</span> · Pago: <span className="font-mono">{fmtBRL(selectedTx.paid_amount || 0)}</span> · Restante: <span className="font-mono font-semibold text-foreground">{fmtBRL(selectedTx.amount - (selectedTx.paid_amount || 0))}</span>
            </div>
          )}
          <form onSubmit={submitPay} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor</Label>
                <Input data-testid={TX.payAmount} type="number" step="0.01" min="0.01" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Data</Label>
                <Input data-testid={TX.payDate} type="date" required value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Método</Label>
                <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                  <SelectTrigger data-testid={TX.payMethod}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['PIX', 'TED', 'DOC', 'BOLETO', 'CARTAO', 'DINHEIRO', 'TRANSFERENCIA', 'OUTRO'].map((m) =>
                      <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Conta</Label>
                <Select value={payForm.account_id} onValueChange={(v) => setPayForm({ ...payForm, account_id: v })}>
                  <SelectTrigger data-testid={TX.payAccount}><SelectValue /></SelectTrigger>
                  <SelectContent>{(accounts || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Observações</Label>
                <Textarea value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPayOpen(false)}>Cancelar</Button>
              <Button data-testid={TX.paySubmit} type="submit" disabled={payMut.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {payMut.isPending ? 'Registrando…' : 'Confirmar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* DETAIL MODAL */}
      <TransactionDetailDialog open={detailOpen} onOpenChange={setDetailOpen} tx={selectedTx} />
    </div>
  );
}

function TransactionDetailDialog({ open, onOpenChange, tx }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data } = useQuery({
    queryKey: ['tx-detail', tx?.id],
    enabled: !!tx?.id && open,
    queryFn: async () => (await api.get(`/transactions/${tx.id}`)).data,
  });

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append('attachable_type', 'transaction');
      fd.append('attachable_id', tx.id);
      fd.append('file', file);
      const r = await api.post('/attachments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      return r.data;
    },
    onSuccess: () => { toast.success('Comprovante anexado'); qc.invalidateQueries({ queryKey: ['tx-detail'] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/attachments/${id}`)).data,
    onSuccess: () => { toast.success('Removido'); qc.invalidateQueries({ queryKey: ['tx-detail'] }); },
  });

  const download = (a) => {
    const token = localStorage.getItem('ff_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${a.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const link = document.createElement('a');
        link.href = url; link.download = a.original_name; link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Detalhes da transação</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 text-center text-muted-foreground">Carregando…</div> : (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Descrição</div><div className="font-medium">{data.transaction.description}</div></div>
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Valor</div><div className="font-mono tabular-nums font-semibold">{fmtBRL(data.transaction.amount)}</div></div>
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Vencimento</div><div>{fmtDate(data.transaction.due_date)}</div></div>
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Status</div><span className={`chip ${statusColor[data.transaction.status]}`}>{statusLabel[data.transaction.status]}</span></div>
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Pago</div><div className="font-mono">{fmtBRL(data.transaction.paid_amount || 0)}</div></div>
              <div><div className="text-[10px] uppercase font-mono text-muted-foreground">Restante</div><div className="font-mono">{fmtBRL(Math.max(0, data.transaction.amount - (data.transaction.paid_amount || 0)))}</div></div>
            </div>

            <div>
              <div className="text-sm font-semibold mb-2">Pagamentos</div>
              {data.payments.length === 0 ? <div className="text-xs text-muted-foreground">Nenhum pagamento ainda.</div> : (
                <div className="divide-y border rounded-md">
                  {data.payments.map((p) => (
                    <div key={p.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${p.cancelled ? 'opacity-50 line-through' : ''}`}>
                      <div className="font-mono tabular-nums">{fmtBRL(p.amount)}</div>
                      <div className="text-muted-foreground">{fmtDate(p.payment_date)} · {p.payment_method}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold">Comprovantes</div>
                <div>
                  <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMut.mutate(f); e.target.value = ''; }} />
                  <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploadMut.isPending}>
                    <Upload className="h-3.5 w-3.5 mr-1" /> {uploadMut.isPending ? 'Enviando…' : 'Anexar'}
                  </Button>
                </div>
              </div>
              {data.attachments.length === 0 ? <div className="text-xs text-muted-foreground">Nenhum arquivo.</div> : (
                <div className="grid grid-cols-2 gap-2">
                  {data.attachments.map((a) => (
                    <div key={a.id} className="border rounded-md p-2 flex items-center gap-2 text-sm">
                      <Paperclip className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0"><div className="truncate">{a.original_name}</div><div className="text-xs text-muted-foreground">{(a.size / 1024).toFixed(0)} KB</div></div>
                      <Button size="icon" variant="ghost" onClick={() => download(a)}><Download className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-rose-500" onClick={() => { if (confirm('Excluir arquivo?')) deleteMut.mutate(a.id); }}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
